import { ApolloClient, ApolloLink, InMemoryCache, Observable, split } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { BASE_URL, DEFAULT_VERSION, HOME_URL } from '@/lib/constants';
import { createUploadLink } from 'apollo-upload-client';
import { getAppPlatform } from '@/lib/platform';
import * as Sentry from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { print } from 'graphql';

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

const graphQLEndpoint = BASE_URL;
const broadcastEndpoint = HOME_URL;

if (!graphQLEndpoint) {
  throw new Error('VITE_API_URL is not configured.');
}

if (!broadcastEndpoint) {
  throw new Error('VITE_HOME_URL is not configured.');
}

const uploadLink = createUploadLink({
  uri: `${graphQLEndpoint}/graphql`,
  credentials: 'include',
  fetchOptions: {
    credentials: 'include',
  },
});

let currentAuthToken: string | null = null;
let currentLocale = 'en';
let currentVersion = '';
const currentPlatform = getAppPlatform();
let echo: Echo<'reverb'> | null = null;

function getXsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function getRequestHeaders(
  headers: Record<string, string> = {},
  options?: { includeAuthorization?: boolean }
): Record<string, string> {
  const xsrfToken = getXsrfToken();
  const includeAuthorization = options?.includeAuthorization ?? true;

  return {
    ...headers,
    ...(includeAuthorization && currentAuthToken
      ? { Authorization: `Bearer ${currentAuthToken}` }
      : {}),
    ...(getAppPlatform() === 'web' && xsrfToken
      ? { 'X-XSRF-TOKEN': xsrfToken }
      : {}),
    'Accept-Language': currentLocale,
    'X-App-Version': currentVersion || DEFAULT_VERSION,
    'X-App-Platform': currentPlatform,
  };
}

function getEchoInstance(): Echo<'reverb'> {
  if (echo) {
    return echo;
  }

  if (typeof window !== 'undefined') {
    (window as typeof window & { Pusher?: typeof Pusher }).Pusher = Pusher;
  }

  echo = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: import.meta.env.VITE_REVERB_HOST,
    wssPort: Number(import.meta.env.VITE_REVERB_PORT),
    wsPort: Number(import.meta.env.VITE_REVERB_PORT),
    forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
    authorizer: (channel) => ({
      authorize: async (socketId, callback) => {
        try {
          const res = await fetch(`${broadcastEndpoint}/broadcasting/auth`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              ...getRequestHeaders({}, { includeAuthorization: false }),
            },
            body: JSON.stringify({
              socket_id: socketId,
              channel_name: channel.name,
            }),
          });

          const data = await res.json().catch(() => null);

          if (!res.ok) {
            const error = new Error(
              data?.message || `Broadcast auth failed (${res.status})`
            );
            callback(error, null);
            return;
          }

          callback(null, data);
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          callback(error, null);
        }
      },
    }),
  });

  return echo;
}

const authLink = setContext((_, { headers }) => ({
  headers: {
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...getRequestHeaders(headers),
  },
}));

const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  Sentry.addBreadcrumb({
    category: 'graphql.' + (definition.kind === 'OperationDefinition' ? definition.operation : '??'),
    message: operation.operationName,
    data: operation.variables,
    level: 'info',
  });
  return forward(operation);
});

const sentryErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const definition = getMainDefinition(operation.query);
  const operationType = definition.kind === 'OperationDefinition' ? definition.operation : 'unknownOperation';
  const category = `graphql.${operationType}.${operation.operationName}`;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, extensions }) => {
      SentryVue.addBreadcrumb({
        category,
        message: `[GraphQL error] ${message}`,
        data: { variables: operation.variables, locations, extensions },
        level: 'error',
      });
    });
  }

  if (networkError) {
    SentryVue.addBreadcrumb({
      category,
      message: `[Network error] ${networkError}`,
      data: { variables: operation.variables },
      level: 'error',
    });
  }

  if (import.meta.env.DEV) {
    console.error('[Apollo Error]', { graphQLErrors, networkError });
  } else {
    console.error(JSON.stringify({ graphQLErrors, networkError }));
  }
});

const subscriptionsLink = new ApolloLink((operation) =>
  new Observable((observer) => {
    const abortController = new AbortController();
    let channelName: string | null = null;
    let isActive = true;

    (async () => {
      try {
        const response = await fetch(`${graphQLEndpoint}/graphql`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...getRequestHeaders(),
          },
          body: JSON.stringify({
            operationName: operation.operationName,
            variables: operation.variables,
            query: print(operation.query),
          }),
          signal: abortController.signal,
        });

        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload?.message ?? 'Failed to initialize subscription.');
        }

        if (payload?.errors) {
          observer.error(payload.errors);
          return;
        }

        channelName = payload?.extensions?.lighthouse_subscriptions?.channel ?? null;

        if (!channelName) {
          throw new Error('Missing Lighthouse subscription channel in response.');
        }

        const echoInstance = getEchoInstance();

        echoInstance
          .channel(channelName)
          .listen('.lighthouse-subscription', (eventPayload: { result?: unknown }) => {
            if (!isActive || !eventPayload || typeof eventPayload !== 'object' || !('result' in eventPayload)) {
              return;
            }

            observer.next(eventPayload.result as Record<string, unknown>);
          });
      } catch (error) {
        if (!isActive || (error instanceof DOMException && error.name === 'AbortError')) {
          return;
        }

        observer.error(error);
      }
    })();

    return () => {
      isActive = false;
      abortController.abort();

      if (channelName) {
        getEchoInstance().leaveChannel(channelName);
      }
    };
  }),
);

export function setAuthToken(token?: string | null) {
  currentAuthToken = token ?? null;
  echo?.disconnect();
  echo = null;
}

export function setLocale(locale: string) {
  currentLocale = locale;
}

export function setVersion(version: string) {
  currentVersion = version;
}

const httpLink = uploadLink as unknown as ApolloLink;

const terminatingLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  subscriptionsLink,
  httpLink,
);

const apolloLinkChain: ApolloLink = ApolloLink.from([
  sentryBreadcrumbLink,
  sentryErrorLink,
  authLink,
  terminatingLink,
]);

export const apolloClient = new ApolloClient({
  link: apolloLinkChain,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          plotPosts: relayStylePagination(['plotSlug']),
        },
      },
    },
  }),
});